import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import { SCROLL_TO_OFFERS_QUERYPARAM } from 'src/app/constants/app.constants';
import { Alert } from 'src/app/interfaces/alert';
import { Pet, PetSpeciesFilterableType } from 'src/app/interfaces/pet';
import { PetplanTrialApplication } from 'src/app/interfaces/petplan';
import { DeclarationChecks, User, WelcomeGiftStepState } from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { UserService } from 'src/app/services/user/user.service';
import { PetplanDeclineInsuranceModalComponent } from 'src/app/modals/petplan-decline-insurance-modal/petplan-decline-insurance-modal.component';
import { PetplanService } from 'src/app/services/petplan/petplan.service';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE } from 'src/app/constants/route.constants';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-petplan-welcome-gift',
  templateUrl: './petplan-welcome-gift.component.html',
  styleUrls: ['./petplan-welcome-gift.component.scss'],
})
export class PetplanWelcomePackComponent {
  public alert: { type: string; message: SafeHtml } | null = null;
  public loading = false;
  public user: User;
  public eligiblePets: Pet[];
  public petNames: string;
  public petSpecie: string;
  public bannerImagePath: string;
  public eligiblePet: Pet;
  public showPetPlanHalfPage: boolean;
  public declarationChecks: DeclarationChecks = {
    microchipWasAccurate: false,
    noPreExistingConditions: false
  }
  public declarationChecksAllTrue: boolean;
  public isDataUsageAllowed: boolean = false;
  private isBrowser;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private logService: LogService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private petplanService: PetplanService,
    private _modalService: NgbModal,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.activatedRoute.snapshot.data.user as User;
    this.eligiblePets = this.activatedRoute.snapshot.data.eligiblePets as Pet[];
    this.eligiblePet = this.getLastItem();

    this.bannerImagePath = this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog
    ? "/assets/images/partner/petplan/banner/banner-dog.png"
    : this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat
    ? "/assets/images/partner/petplan/banner/banner-cat.png"
    : "/assets/images/partner/petplan/banner/banner-rabbit.png"

    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.petNames = this.createTitle();
    if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog) {
      this.petSpecie = "Dog";
    } else if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat) {
      this.petSpecie = "Cat";
    } else {
      this.petSpecie = "Rabbit";
    }
  }

  private createTitle(): string {
    if (!!this.eligiblePets && this.eligiblePets.length > 0) {
      return `${this.createPetsListString(this.eligiblePets)}.`;
    }

    // If there are no eligible pets, redirect to dashboard.
    this.alert = {
      type: 'info',
      message:
        'You do not have any eligible pets for petplan. You\'ll get redirected in a few seconds!',
    };
    setTimeout(() => {
      this.router.navigate(['/dashboard'], { queryParams: { followUs: 'open' } });
    }, 1000);
    return;
  }

  private createPetsListString(pets): string {
    if (!pets.length) {
      return '';
    } else if (pets.length === 1) {
      return pets[0].name;
    } else if (pets.length === 2) {
      return `${pets[0].name} & ${pets[1].name}`;
    } else if (pets.length === 3) {
      return `${pets[0].name}, ${pets[1].name} & ${pets[2].name}`;
    } else {
      return `${pets[0].name}, ${pets[1].name} & your other pets`;
    }
  }

  public noToPetInsurance() {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
    };

    const modalRef = this._modalService.open(PetplanDeclineInsuranceModalComponent, modalOptions);

    modalRef.componentInstance.eligiblePet = this.eligiblePet
    modalRef.result.then(
    (result) => {
      result ? this.openHalfPageDeclaration() :
       this.completeWelcomeGift(result);
      },
    );
  }

  public completeWelcomeGift(claimGift: boolean) {
    if (!this.loading) {
      this.updateUserDetails()
        .subscribe(
          (updatedUser) => {
            this.user = updatedUser;
            this.alert = {
              type: 'success',
              message:
                'Your account information was updated. You\'ll get redirected in a few seconds!',
            };
            setTimeout(() => {
              claimGift
                ? this.sendPetplanApplication()
                : this.router.navigate(['/dashboard'], {
                    queryParams: { scrollTo: SCROLL_TO_OFFERS_QUERYPARAM, followUs: 'open' },
                  });
            }, 1000);
            return;
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
            this.logService.error(
              'An error has occurred during welcome gift state save. Please try again later. ',
              err
            );
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public closeAlert() {
    this.alert = null;
  }

  public toggleYesNo() {
    this.isDataUsageAllowed = !this.isDataUsageAllowed;
  }

  public onDeclarationChange(event: Event, field: keyof DeclarationChecks): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.declarationChecks[field] = checked;
    this.declarationChecksAllTrue = Object.values(this.declarationChecks).every(value => value === true);
  }

  public openHalfPageDeclaration() {
    this.loading = true;
    this.petplanService.petplanSave().subscribe({
      next: () => {
        this.loading = false;
        this.showPetPlanHalfPage = true;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
        const errorList = err?.error || [];
        let unknownErrorShown = false;

        let messages: string[] = [];
    
        for (const error of errorList) {
          const key = error?.error_message;
          const rawMessage = errorMessagesMap[key];

          const msg = rawMessage?.replace('{petId}', this.eligiblePet.id.toString());

          if (msg) {
            messages.push(msg);
          } else if (!unknownErrorShown) {
            messages.push("We're unable to process your request. Please reach out to our support team either by emailing hello@identibase.co.uk or call us on 0330 9128077.");
            unknownErrorShown = true;
          }
        }
    
        this.alert = {
          type: 'danger',
          message: this.sanitizer.bypassSecurityTrustHtml(messages.join('<br>'))
        };
      },
    });
  }

  private updateUserDetails(): Observable<User> {
    this.closeAlert();
    this.loading = true;
    const userChanges: Partial<User> = {
      welcomeGiftStepCompleted:
        this.user.welcomeGiftStepCompleted === WelcomeGiftStepState.NotUpdated
          ? WelcomeGiftStepState.Updated
          : this.user.welcomeGiftStepCompleted,
    };

    return this.userService.patchMe(userChanges);
  }

  private getLastItem(): Pet {
    return this.eligiblePets.length ? this.eligiblePets[this.eligiblePets.length - 1] : null;
  }

  private sendPetplanApplication() {
    this.closeAlert();
    this.loading = true;
    if (this.user.pets.length < 1){
      this.alert = {
        type: 'danger',
        message: 'You do not have any pets registered in our system. Please come back after you have registered your pet.',
      };
      return;
    }

    const request: PetplanTrialApplication = {
      isDataUsageAllowed: this.isDataUsageAllowed
    };

    this.petplanService.submitPetplanTrialApplication(request).subscribe({
      next: () => {
        if (this.isBrowser) {
          this.loading = false;
          const arrivedFromEmail = localStorage.getItem("arrived_from_email");
  
          if (arrivedFromEmail) {
            this.router.navigate(
              ['/account/' + IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE],
              { queryParams: { subscriptionType: 'petplan4weeksfree' } }
            );
          } else {
            this.router.navigate(
              ['/dashboard'],
              { queryParams: { followUs: 'open' } }
            );
          }
        }
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
        const errorList = err?.error || [];
        let unknownErrorShown = false;

        let messages: string[] = [];
    
        for (const error of errorList) {
          const key = error?.error_message;
          const rawMessage = errorMessagesMap[key];

          const msg = rawMessage?.replace('{petId}', this.eligiblePet.id.toString());

          if (msg) {
            messages.push(msg);
          } else if (!unknownErrorShown) {
            messages.push("We're unable to process your request. Please reach out to our support team either by emailing hello@identibase.co.uk or call us on 0330 9128077.");
            unknownErrorShown = true;
          }
        }
    
        this.alert = {
          type: 'danger',
          message: this.sanitizer.bypassSecurityTrustHtml(messages.join('<br>'))
        };
      },
    });
  }

  public getProgressData(){
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: true,
      isMembershipSelectionCompleted: true,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }
}
